import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Notification,
  NotificationContextType,
  NotificationFilter,
} from '../types/notification';

const STORAGE_KEY = 'panther_notifications';
const MAX_NOTIFICATIONS = 100; // Limit stored notifications

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Load notifications from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        const withDates = parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp),
        }));
        setNotifications(withDates);
      }
    } catch (error) {
      console.error('Failed to load notifications from localStorage:', error);
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Failed to save notifications to localStorage:', error);
    }
  }, [notifications]);

  const addNotification = useCallback((
    notificationData: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ) => {
    const newNotification: Notification = {
      ...notificationData,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };

    setNotifications((prev) => {
      const updated = [newNotification, ...prev];
      // Limit to MAX_NOTIFICATIONS
      return updated.slice(0, MAX_NOTIFICATIONS);
    });
  }, []);

  // Listen for custom notification events
  useEffect(() => {
    const handleNotificationEvent = (event: any) => {
      const { title, message, type, eventType, metadata } = event.detail;
      addNotification({ title, message, type, eventType, metadata });
    };

    window.addEventListener('addNotification', handleNotificationEvent);
    return () => {
      window.removeEventListener('addNotification', handleNotificationEvent);
    };
  }, [addNotification]);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const deleteNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getFilteredNotifications = useCallback(
    (filter?: NotificationFilter): Notification[] => {
      if (!filter) return notifications;

      return notifications.filter((notification) => {
        // Filter by type
        if (filter.type && notification.type !== filter.type) {
          return false;
        }

        // Filter by event type
        if (filter.eventType && notification.eventType !== filter.eventType) {
          return false;
        }

        // Filter by read status
        if (filter.read !== undefined && notification.read !== filter.read) {
          return false;
        }

        // Filter by date range
        if (filter.dateFrom && notification.timestamp < filter.dateFrom) {
          return false;
        }
        if (filter.dateTo && notification.timestamp > filter.dateTo) {
          return false;
        }

        // Filter by search query
        if (filter.searchQuery) {
          const query = filter.searchQuery.toLowerCase();
          const matchesTitle = notification.title.toLowerCase().includes(query);
          const matchesMessage = notification.message.toLowerCase().includes(query);
          if (!matchesTitle && !matchesMessage) {
            return false;
          }
        }

        return true;
      });
    },
    [notifications]
  );

  const unreadCount = notifications.filter((n) => !n.read).length;

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    getFilteredNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
